/* eslint-disable max-lines-per-function */
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import { useToast } from '@/hooks/use-toast';
import { useProfile } from '@/hooks/useProfile';
import { useCreateTermine } from '@/domain/termine/hooks/useCreateTermine';
import { FormValues } from '@/domain/termine/shared/types/CreateTermineInput';
import { dateMapper } from '@/domain/termine/features/CreateTermin/Sheet/utils/formMappers';
import { Form } from '@/components/ui/form';
import {
  markierungTypes,
  TerminWiederholung,
} from '@/domain/termine/shared/types';
import { CreateTermineFormContext } from '@/domain/termine/features/CreateTermin';
import { useStandortContext } from '@/features/standortContext/StandortContext';

interface CreateTermineFormProviderProps {
  children: ReactNode;
}

const fileToBase64 = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      try {
        const result = reader.result.toString();
        const base64 = result.split(',')[1];
        if (!base64) {
          reject(new Error('Invalid file format'));
          return;
        }
        resolve(base64);
      } catch {
        reject(new Error('Failed to process file'));
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

export function CreateTermineFormProvider({
  children,
}: CreateTermineFormProviderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { manager } = useProfile();
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { toast } = useToast();
  const { createTermin, loading } = useCreateTermine();

  const {
    year,
    month,
    datum: datumParam,
  } = useParams<{
    year: string;
    month: string;
    datum?: string;
  }>();

  const defaultDate = datumParam
    ? dayjs(datumParam).toDate()
    : dayjs().toDate();

  const form = useForm<FormValues>({
    defaultValues: {
      titel: '',
      beschreibung: '',
      datum: defaultDate.toISOString(),
      beginn: '',
      ende: '',
      standortId: standortId,
      markierung: markierungTypes[19],
      specials: '',
      wiederholung: TerminWiederholung.OHNE,
      wiederholungBis: undefined,
      terminBild: null,
    },
    mode: 'onChange',
  });

  function formValuesToTerminInput(data: FormValues): FormValues {
    const mapped = dateMapper(data);

    const terminInput: FormValues = {
      titel: data.titel,
      beschreibung: data.beschreibung || '',
      datum: mapped.dateOnly,
      beginn: mapped.beginnDateTime || '',
      ende: mapped.endeDateTime || '',
      standortId: data.standortId,
      markierung: data.markierung,
      specials: data.specials || '',
      wiederholung: data.wiederholung || TerminWiederholung.OHNE,
      wiederholungBis: mapped.wiederholungBisTime,
      terminBild: data.terminBild,
    };

    return terminInput;
  }

  async function handleSubmit() {
    const data = form.getValues();

    let terminBildInput: { name: string; base64Image: string } | undefined;
    if (data.terminBild?.file) {
      const base64Data = await fileToBase64(data.terminBild.file);
      terminBildInput = {
        name: data.terminBild.name,
        base64Image: base64Data,
      };
    }

    const terminInput = {
      ...formValuesToTerminInput(data),
      terminBildInput: terminBildInput,
    };

    const response = await createTermin({
      managerId: manager.id,
      termin: terminInput,
    });

    if (response.error) {
      toast({
        title: t('domain.termine.sheet.messages.terminErstellt.error.title'),
        description:
          response.error.message ||
          t('domain.termine.sheet.messages.terminErstellt.error.description'),
        variant: 'destructive',
      });
    } else {
      toast({
        title: t('domain.termine.sheet.messages.terminErstellt.title'),
        description: t(
          'domain.termine.sheet.messages.terminErstellt.description',
        ),
        variant: 'default',
      });
      navigate(`/termine/uebersicht/${year}/${month}`);
    }
  }

  return (
    <CreateTermineFormContext.Provider
      value={{ loading, onSubmit: handleSubmit, form }}>
      <Form {...form}>{children}</Form>
    </CreateTermineFormContext.Provider>
  );
}
